const HEX_RE = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const expandShortHex = (body: string): string => {
  if (body.length === 3) {
    return body
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return body;
};

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const trimmed = hex.trim();
  const match = trimmed.match(HEX_RE);
  if (!match) {
    throw new Error(`hexToRgb: invalid hex "${hex}"`);
  }
  const body = expandShortHex(match[1]);
  return {
    r: Number.parseInt(body.slice(0, 2), 16),
    g: Number.parseInt(body.slice(2, 4), 16),
    b: Number.parseInt(body.slice(4, 6), 16),
  };
}

export function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = (gn - bn) / d + (gn < bn ? 6 : 0);
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      default:
        h = (rn - gn) / d + 4;
        break;
    }
    h /= 6;
  }

  return { h: h * 360, s, l };
}

const hueToRgb = (p: number, q: number, t: number): number => {
  let tt = t;
  if (tt < 0) tt += 1;
  if (tt > 1) tt -= 1;
  if (tt < 1 / 6) return p + (q - p) * 6 * tt;
  if (tt < 1 / 2) return q;
  if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
  return p;
};

export function hslToHex(h: number, s: number, l: number): string {
  const hn = ((h % 360) + 360) % 360 / 360;
  const sn = Math.min(1, Math.max(0, s));
  const ln = Math.min(1, Math.max(0, l));

  if (sn === 0) {
    const v = Math.round(ln * 255);
    const x = v.toString(16).padStart(2, '0');
    return `#${x}${x}${x}`;
  }

  const q = ln < 0.5 ? ln * (1 + sn) : ln + sn - ln * sn;
  const p = 2 * ln - q;
  const r = Math.round(hueToRgb(p, q, hn + 1 / 3) * 255);
  const g = Math.round(hueToRgb(p, q, hn) * 255);
  const b = Math.round(hueToRgb(p, q, hn - 1 / 3) * 255);

  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function lighten(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const { h, s, l } = rgbToHsl(r, g, b);
  const nextL = Math.min(1, Math.max(0, l + amount));
  return hslToHex(h, s, nextL);
}

export function darken(hex: string, amount: number): string {
  return lighten(hex, -amount);
}

const channelLinear = (c: number): number => {
  const cs = c / 255;
  return cs <= 0.04045 ? cs / 12.92 : ((cs + 0.055) / 1.055) ** 2.4;
};

const relativeLuminance = (hex: string): number => {
  const { r, g, b } = hexToRgb(hex);
  const R = channelLinear(r);
  const G = channelLinear(g);
  const B = channelLinear(b);
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

export function getContrastRatio(hex1: string, hex2: string): number {
  const L1 = relativeLuminance(hex1);
  const L2 = relativeLuminance(hex2);
  const light = Math.max(L1, L2);
  const dark = Math.min(L1, L2);
  if (dark <= 0) return light > 0 ? 21 : 1;
  return (light + 0.05) / (dark + 0.05);
}

const STOP_ORDER = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const;

const normalizeHex = (hex: string): string => {
  const h = hex.trim().replace(/^#/, '');
  const body = expandShortHex(h);
  return `#${body.toLowerCase()}`;
};

/**
 * Builds a Tailwind-style ramp (50–950) from a base hex anchored at the 500 stop.
 */
export function generateColorScale(baseHex: string): Record<string, string> {
  const normalized = normalizeHex(baseHex);
  const { r, g, b } = hexToRgb(normalized);
  const { h, s, l: l500 } = rgbToHsl(r, g, b);

  const lightnessDelta: Record<(typeof STOP_ORDER)[number], number> = {
    50: 0.44,
    100: 0.38,
    200: 0.3,
    300: 0.22,
    400: 0.11,
    500: 0,
    600: -0.09,
    700: -0.15,
    800: -0.22,
    900: -0.28,
    950: -0.34,
  };

  const result: Record<string, string> = {};
  for (const stop of STOP_ORDER) {
    if (stop === 500) {
      result['500'] = normalized;
      continue;
    }
    const L = Math.min(0.985, Math.max(0.04, l500 + lightnessDelta[stop]));
    const satFactor = stop < 500 ? 0.25 + (stop / 500) * 0.75 : 1;
    const sat = Math.min(1, Math.max(0, s * satFactor));
    result[String(stop)] = hslToHex(h, sat, L);
  }
  return result;
}
